import { Component, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import { BackgroundMode } from '@ionic-native/background-mode';
import { ILatLng, Spherical } from '@ionic-native/google-maps';
import { TranslateService } from '@ngx-translate/core';
import { Events, Platform } from 'ionic-angular';
import { Observable, Subscription } from 'rxjs';
import 'rxjs/add/observable/timer';
import { MapUtil } from '../../map/map-util';
import { MapWrapper } from '../../map/map-wrapper';
import { TotvsNotificationProvider } from '../../providers/totvs-notification-provider';
import { TotvsNotificationType } from '../../providers/totvs-notification-type';
import { MapProvider } from './../../providers/map';
import { PontosShapeListController } from './shape-draw-controller';

const HTML_TEMPLATE = `
<ion-content>
  <div #map id="map"></div>
  
  <button #elementArea class="area restart"></button>
  
  <div [hidden]="!isOnlyDraw">
    <ion-grid class="grid">
      <ion-row>
        <ion-col class="col-restart">
          <button #buttonRestart class="btn restart" ion-button (click)="cleanPolygon()">{{translate.restart}}</button>
        </ion-col>
        <ion-col class="col-save" (click)="save()">
          <button #buttonSave class="btn save" ion-button>{{translate.save}}</button>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
  <div [hidden]="isOnlyDraw" style="position: fixed; width: 100%; bottom: 0px; background-color: #ffffff">
    <p class="caminhe-o-permetro">{{translate.walkPerimeter}}</p>
    <ion-grid class="grid">
      <ion-row>
        <ion-col>
          <button class="btn-walk restart" ion-button outline (click)="cancelTracking()">{{translate.cancel}}</button>
        </ion-col>
        <ion-col>
          <button #buttonConclude class="btn-walk save" ion-button (click)="concludeTracking()">{{translate.conclude}}</button>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
  <ion-fab #deletePoint right top class="fb-delete" (click)="deleteSelectedPoint()">
    <button ion-fab class="thf-fab-button">
        <ion-icon name="trash"></ion-icon>     
    </button>
  </ion-fab>
  <ion-fab #walkPoint left top class="fb-walk" (click)="walkDrawOption()">
    <button ion-fab class="thf-fab-button">
      <ion-icon name="walk"></ion-icon>
    </button>
  </ion-fab>
</ion-content>
`;

const CSS_STYLE = `
ion-content .scroll-content{
    overflow-y: hidden !important;
}
#map {
    width: 100%;
    height: 100%;
}
    
.btn {
    position: fixed;
    bottom: 14px;        
    width: calc(50% - 20px - 20px);
}

.btn-walk {
    width: 100%;
}

.col-restart {
    padding: 0px 0px 0px 24px !important;
}

.restart{
    background-color: #ffffff;
    color: #0c9abe;
    border-color: #0c9abe;
}

.col-save {
    padding: 0px 24px 0px 15px !important;
}

.save{
    background-color: #b6bdbf;
}

.fb-delete{
    visibility: hidden; margin-top: 60px;
}

ion-fab {
	margin: 8px;
}
	
.thf-fab-button {
	background-color: #ffffff;
	color: #0c9abe;
}

.icon-totvs{
    font-size: 30px;
    padding-left: 10px;
    padding-top: 4px;
}

.fb-walk{
     margin-top: 60px;
}

.caminhe-o-permetro {
    font-family: NunitoSans;
    font-size: 16px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: #4a5c60;
    padding: 24px;
}

.area {
    position: fixed;
    top: 77px;
    border-radius: 32px;
    margin-left: 10px;
    font-weight: bold;  
    padding-left: 16px;     
    padding-right: 16px;    
    opacity: 0.75; 
}

@supports (-webkit-overflow-scrolling: touch) {

    .fb-walk {
        margin-top: 82px !important;
    }
    
    .fb-delete {
        margin-top: 82px !important;
    }

    .area {
        top: 97px !important;
    }
}
`;

@Component({
    selector: 'shape-draw',
    template: HTML_TEMPLATE,
    styles: [CSS_STYLE]
})
export class ShapeDrawComponent {

    @ViewChild('map') mapElement: ElementRef;
    @ViewChild('buttonSave', { read: ElementRef }) buttonSave: ElementRef;
    @ViewChild('buttonConclude', { read: ElementRef }) buttonConclude: ElementRef;

    @ViewChild('buttonRestart', { read: ElementRef }) buttonRestart: ElementRef;
    @ViewChild('deletePoint', { read: ElementRef }) deletePoint: ElementRef;
    @ViewChild('walkPoint', { read: ElementRef }) walkPoint: ElementRef;

    @ViewChild('elementArea', { read: ElementRef }) elementArea: ElementRef;

    translate: any = {};
    @Input('saveClick') saveClick: Function;
    @Input('backgroundPolygons') backgroundPolygons: ILatLng[][];

    points: Array<ILatLng>;
    private pontosShape: PontosShapeListController;

    private _observableArea;

    editPolygon: Subscription;

    isStart: boolean;
    isFinish: boolean;
    isWalkDraw: boolean;
    isOnlyDraw: boolean;

    private labelSiglaHectare: string;

    private trackObs: Subscription;

    private map: MapWrapper;

    constructor(public platform: Platform,
        private _notification: TotvsNotificationProvider,
        private _events: Events,
        private _translate: TranslateService,
        private backgroundMode: BackgroundMode,
        private _mapProvider: MapProvider) {

        this.isOnlyDraw = true;
        this._events.subscribe("shape-draw:deletePoint", () => {
            this.changeButtonVisibility(this.deletePoint, true);
        });

        setTimeout(() => {
            this.labelSiglaHectare = " " + this._translate.instant("COMMON.SIGLA_HECTARES");

            this.translate = {
                restart: this._translate.instant("COMMON.RESTART"),
                cancel: this._translate.instant("COMMON.CANCEL"),
                save: this._translate.instant("COMMON.SAVE"),
                conclude: this._translate.instant("COMMON.CONCLUDE"),
                walkPerimeter: this._translate.instant("COMMON.WALK_PERIMETER")
            }
        }, 400);

    }

    async ngOnInit() {
        await this.platform.ready();
        await this.loadMap();
        this.changeButtonColor();


    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['backgroundPolygons']) {
            this.drawBackgroundPolygons(true);
        }
    }

    drawBackgroundPolygons(repositioning?: boolean) {
        if (this.backgroundPolygons) {
            this.map.clearMap();

            this.backgroundPolygons.forEach(polygon => {
                let polygonOptions = {
                    points: polygon,
                    strokeColor: 'rgba(255, 255, 255, 0.50)',
                    fillColor: 'rgba(255, 255, 255, 0.25)',
                    strokeWidth: 2,
                    clickable: false
                };
                this.map.getMapObject().addPolygonSync(polygonOptions);
            });

            if (repositioning) {
                let points = this.backgroundPolygons.reduce((latLng1, latLng2) => latLng1.concat(latLng2), []);
                this.map.moveCamera({
                    'target': points
                });
            }
        }
    }

    cleanPolygon() {
        if (this.editPolygon)
            this.editPolygon.unsubscribe();

        this.isFinish = false;
        this.isStart = false;

        this.isOnlyDraw = true

        this.pontosShape.clear();
        this.drawBackgroundPolygons();

        this.changeButtonColor();

        this.changeButtonVisibility(this.deletePoint, false);
        this.changeButtonVisibility(this.walkPoint, true);

        this.observablePolygonArea("end");
    }

    save() {
        if (this.pontosShape.isPolygonDrew()) {

            if (this.saveClick) {
                this.saveClick({ polygon: this.pontosShape.getSimplifiedPoints(), areaPolygon: this.pontosShape.getAreaPolygon() });
            }
            else
                console.log('Não possui nenhuma evento a ser lançado');

        } else {
            this._notification.toast(this._translate.instant("COMMON.UNFORMED_POLYGON"), { type: TotvsNotificationType.ERROR });
        }
    }

    changeButtonColor() {
        if (this.buttonSave) {
            if (this.pontosShape.isPolygonDrew()) {
                this.buttonSave.nativeElement.style.backgroundColor = "#0c9abe";
                this.buttonSave.nativeElement.style.opacity = "1";
                this.changeButtonVisibility(this.elementArea, true);
            } else {
                this.buttonSave.nativeElement.style.backgroundColor = "#b6bdbf";
                this.buttonSave.nativeElement.style.opacity = ".7";
                this.changeButtonVisibility(this.elementArea, false);
            }
        }

        if (this.buttonConclude) {
            if (this.pontosShape.isPolygonDrew()) {
                this.buttonConclude.nativeElement.style.backgroundColor = "#0c9abe";
                this.buttonConclude.nativeElement.style.opacity = "1";
                this.changeButtonVisibility(this.elementArea, true);
            } else {
                this.buttonConclude.nativeElement.style.backgroundColor = "#b6bdbf";
                this.buttonConclude.nativeElement.style.opacity = ".7";
                this.changeButtonVisibility(this.elementArea, false);
            }
        }

        if (this.buttonRestart) {
            if (this.pontosShape.getLatLngPoints().length || this.isFinish) {
                this.buttonRestart.nativeElement.style.backgroundColor = "#ffffff";
                this.buttonRestart.nativeElement.style.color = "#0c9abe";
                this.buttonRestart.nativeElement.style.opacity = "1";
            } else {
                this.buttonRestart.nativeElement.style.backgroundColor = "#b6bdbf";
                this.buttonRestart.nativeElement.style.color = "#ffffff";
                this.buttonRestart.nativeElement.style.opacity = ".7";
            }
        }
    }

    changeButtonVisibility(element: ElementRef, isVisibility: boolean) {
        if (element)
            element.nativeElement.style.visibility = isVisibility ? 'visible' : 'hidden';
    }

    async deleteSelectedPoint() {
        this.pontosShape.removeSelectedPoint();

        this.changeButtonVisibility(this.deletePoint, false);
        this.changeButtonColor();
    }

    async loadMap() {
        this.map = MapUtil.create(this.mapElement, {
            onclick: latLng => {
                if (!this._observableArea)
                    this.observablePolygonArea("start");

                this.changeButtonVisibility(this.walkPoint, false);

                if (this.pontosShape.getSelectedPoint()) {
                    this.pontosShape.unselectPoints();
                    this.changeButtonVisibility(this.deletePoint, false);
                } else if (this.isOnlyDraw) {
                    this.pontosShape.add(latLng[0]);
                }

                this.changeButtonColor();
            }
        });
        this.pontosShape = new PontosShapeListController(this.map.getMapObject(), this._events);

        this._mapProvider.getCurrentPosition().then((currentPosition) => {
            if (this.backgroundPolygons == undefined || this.backgroundPolygons.length == 0) {
                let position: ILatLng = {
                    lat: currentPosition.coords.latitude,
                    lng: currentPosition.coords.longitude
                };
                this.map.moveCamera({ target: [position] });
            }
        });
    }

	/**
	 * Walking
	 */
    walkDrawOption() {

        this.changeButtonVisibility(this.walkPoint, false);

        let buttons = [
            {
                text: this._translate.instant("COMMON.CANCEL"),
                handler: data => {
                    this.changeButtonVisibility(this.walkPoint, true);
                }
            },
            {
                text: this._translate.instant("COMMON.CONTINUE_BUTTON"),
                handler: data => {

                    this.isWalkDraw = true;
                    this.isOnlyDraw = false;
                    this.startTracking();

                }
            }
        ]

        this._notification.presentAlert(this._translate.instant("COMMON.WALK_MODE"), this._translate.instant("COMMON.WALK_MODE_INFO"), buttons, true)
    }

    startTracking() {
        this.backgroundMode.enable();
        this.backgroundMode.disableWebViewOptimizations();
        this.points = [];
        let lastLocation = null;
        let index = 0;
        this.observablePolygonArea("start");
        this.trackObs = Observable.timer(1000, 1000).subscribe(() => {
            this._mapProvider.getCurrentPosition()
                .then(location => {
                    const actualLocation: ILatLng = {
                        lat: location.coords.latitude,
                        lng: location.coords.longitude
                    };
                    if (lastLocation != null) {
                        const distance = Spherical.computeDistanceBetween(lastLocation, actualLocation);
                        if (distance < 5) {
                            return;
                        }
                    }
                    lastLocation = actualLocation;
                    this.points.push(actualLocation);
                    this.pontosShape.add(actualLocation, {
                        drawMiddleMarkers: false,
                        index: index++
                    });
                    this.map.moveCamera({ target: actualLocation });
                    this.changeButtonColor();
                });
        });
    }

    cancelTracking() {
        this.isStart = false;
        this.isFinish = false;

        this.stopTracking();
        this.changeButtonColor();
        this.cleanPolygon();
    }

    getAreaPolygon() {
        return this.pontosShape ? this.pontosShape.getAreaPolygon().toFixed(2) : 0;
    }

    private observablePolygonArea(action: string) {
        if (action == "start") {
            this._observableArea = Observable.interval(100)
                .subscribe(p => {
                    this.elementArea.nativeElement.innerText = this.getAreaPolygon() + this.labelSiglaHectare;
                });
        } else if (this._observableArea) {
            this._observableArea.unsubscribe();
            this._observableArea = null;
        }
    }

    stopTracking() {
        this.backgroundMode.disable();
        this.trackObs.unsubscribe();
        this.observablePolygonArea("end");
    }

    concludeTracking() {
        this.isStart = false;
        this.isFinish = true;
        this.isOnlyDraw = true;

        this.stopTracking();

        if (this.points.length > 0) {
            let listPoints = [].concat(this.points);
            this.points = this.pontosShape.getSimplifiedPointsByList(listPoints);
            this.pontosShape.clear();
            this.drawBackgroundPolygons();
            this.pontosShape.addAll(this.points);
        }

        this.changeButtonColor()
        this._notification.toast(this._translate.instant("COMMON.DRAW_YET"))

    }
}
